import classcat from "classcat";
import React from "react";

import { Button, IconAdd, IconMenu, IconX } from "@touchtech/web-baselayer-ui";

import { Image } from "@is/components/Image";
import { CollectionStyleItemVariant, MediaVariants } from "@is/constants";
import { CollectionStyle } from "@is/types";

import css from "./CollectionStyleItem.module.css";
import useManageCollectionStyleItem from "./hooks/useManageCollectionStyleItem";

type Props = {
  collectionStyle: CollectionStyle;
  variant?: CollectionStyleItemVariant;
  index: number;
  onAdd?: (styleId: string, variantId: string) => void;
  onRemove?: (index: number) => void;
  /**
   * Browse-only rendering: hides the drag handle and the remove button. Used for the items of
   * a brand-wide collection, which store staff may look at but not change.
   */
  readOnly?: boolean;
};

const CollectionStyleItem = ({
  collectionStyle,
  variant = CollectionStyleItemVariant.IN_SEARCH,
  index,
  onAdd,
  onRemove,
  readOnly = false,
}: Props) => {
  const {
    t,
    isPending,
    isTransparent,
    addButtonState,
    handleAddStyleClick,
    handleRemoveStyleClick,
    style,
    setNodeRef,
    attributes,
    listeners,
    collectionItem,
    price,
  } = useManageCollectionStyleItem({
    collectionStyle,
    variant,
    index,
    onAdd,
    onRemove,
  });

  return (
    <div
      ref={setNodeRef}
      className={classcat([
        "text-label-lg",
        css.container,
        { [css.transparent]: isTransparent },
      ])}
      style={style}
    >
      <div className={css.column}>
        {variant === CollectionStyleItemVariant.IN_COLLECTION && !readOnly && (
          <button
            className={classcat(["text-label-xl", css.dragIcon])}
            {...listeners}
            {...attributes}
          >
            <IconMenu />
          </button>
        )}
        <div className={css.imageContainer}>
          <Image
            mediavariant={MediaVariants.THUMBNAIL}
            placeholderMediaVariant={MediaVariants.THUMBNAIL}
            src={collectionItem.mediaUrl}
            layout="cover"
          />
        </div>
        <div
          className={classcat([
            css.styleDetailsContainer,
            {
              [css.loweredWidth]:
                variant === CollectionStyleItemVariant.IN_SEARCH,
            },
          ])}
        >
          <div className={classcat([css.styleName, css.ellipsis])}>
            {collectionItem.styleName}
          </div>
          <div className={css.productInfo}>
            <div className={css.styleId}>{collectionItem.styleId}</div>
            {collectionItem.isNotFullData && collectionItem.variantId ? (
              <div className={css.ellipsis}>{collectionItem.variantId}</div>
            ) : (
              <div className={css.ellipsis}>
                {collectionItem.color.mainColorName
                  ? `${collectionItem.color.mainColorName}`
                  : ""}
                {collectionItem.color.mainColorName && collectionItem.color.name
                  ? " / "
                  : ""}
                {collectionItem.color.name
                  ? `${collectionItem.color.name}`
                  : ""}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className={classcat([css.column, css.rightColumn])}>
        {price && (
          <div
            className={classcat([
              css.price,
              {
                [css.priceContainerWithSale]: !!price.salePrice,
              },
            ])}
          >
            <span
              className={classcat([
                css.retailPrice,
                css.ellipsis,
                { [css.sale]: !!price.salePrice },
              ])}
            >
              {`${price.retailPrice} ${price.currency}`}
            </span>
            {!!price.salePrice && (
              <span
                className={classcat([css.salePrice, css.ellipsis])}
              >{`${price.salePrice} ${price.currency}`}</span>
            )}
          </div>
        )}

        {!collectionItem.isNotFullData && (
          <div className={css.stock}>
            <div
              className={classcat([
                css.stockInfo,
                { [css.negative]: !collectionItem.hasInstoreStock },
              ])}
            >
              {t("In-store")}
            </div>
            <div
              className={classcat([
                css.stockInfo,
                { [css.negative]: !collectionItem.hasOnlineStock },
              ])}
            >
              {t("Online")}
            </div>
          </div>
        )}

        {variant === CollectionStyleItemVariant.IN_COLLECTION && !readOnly && (
          <button
            className={classcat([
              "text-label-xl",
              { [css.pending]: isPending },
            ])}
            disabled={isPending}
            onClick={handleRemoveStyleClick}
          >
            <IconX />
          </button>
        )}

        {variant === CollectionStyleItemVariant.IN_SEARCH && (
          <Button
            variant="secondary"
            state={addButtonState}
            onClick={handleAddStyleClick}
          >
            <IconAdd /> {t("Add")}
          </Button>
        )}
      </div>
    </div>
  );
};

export default CollectionStyleItem;
